import { useState } from "react";
import {
  Box, Card, Typography, Stack, Chip, Divider, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer,
  TablePagination,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  useGetBeauticianWalletBalanceQuery,
  useGetBeauticianTransactionsQuery,
  useAddBeauticianFundsMutation,
} from "slices/beautyApp/beauticianSlice";
import Swal from "sweetalert2";

const statusColor = { completed: "success", pending: "warning", failed: "error" };

export default function WalletTab({ beautician: b }) {
  const userId = b?._id;
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [addOpen, setAddOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [amountError, setAmountError] = useState("");

  const { data: balanceData, refetch: refetchBalance } = useGetBeauticianWalletBalanceQuery(userId, { skip: !userId });
  const { data: txData, refetch: refetchTx } = useGetBeauticianTransactionsQuery(
    { userId, page: page + 1, limit },
    { skip: !userId }
  );
  const [addFunds, { isLoading: adding }] = useAddBeauticianFundsMutation();

  const balance = balanceData?.balance ?? 0;
  const transactions = txData?.transactions || [];
  const totalTx = txData?.pagination?.total || 0;

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
    setAmount(val);
    const num = parseFloat(val);
    if (!val) setAmountError("Amount is required");
    else if (num <= 0) setAmountError("Amount must be greater than 0");
    else setAmountError("");
  };

  const handleAddFunds = async () => {
    const num = parseFloat(amount);
    if (!amount || num <= 0) return;
    try {
      await addFunds({ userId, amount: num, description: description || "Admin credit" }).unwrap();
      refetchBalance();
      refetchTx();
      setAddOpen(false);
      setAmount("");
      setDescription("");
      Swal.fire({ icon: "success", title: "Funds Added", text: `₹${num} added successfully.`, timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: e?.data?.message || "Failed to add funds." });
    }
  };

  const handleOpenAdd = () => {
    setAmount("");
    setDescription("");
    setAmountError("");
    setAddOpen(true);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Balance Card */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ background: "rgba(68,33,65,0.1)", borderRadius: 2, p: 1.5, display: "flex" }}>
              <AccountBalanceWalletIcon sx={{ color: "#442141", fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={1}>
                Wallet Balance
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: balance >= 0 ? "#22c55e" : "#ef4444" }}>
                ₹{balance.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">{b?.name || "Beautician"}</Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ background: "#442141", "&:hover": { background: "#5a2d57" }, textTransform: "none", fontWeight: 600 }}
          >
            Add Funds
          </Button>
        </Stack>
      </Card>

      {/* Transaction History */}
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
        <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: "#442141", borderLeft: "4px solid #FF6584", pl: 1.5 }}>
          Transaction History
        </Typography>
        <TableContainer>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "33%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ background: "#fdf0f5" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: "#442141", px: 1.5, py: 1 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No transactions found.
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx, i) => {
                  const isCredit = tx.type === "credit" || tx.toUserId?._id === userId;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption">{page * limit + i + 1}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption" fontWeight={600} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tx.reference || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {isCredit
                            ? <TrendingUpIcon sx={{ fontSize: 14, color: "#22c55e" }} />
                            : <TrendingDownIcon sx={{ fontSize: 14, color: "#ef4444" }} />}
                          <Typography variant="caption" fontWeight={600} sx={{ color: isCredit ? "#22c55e" : "#ef4444" }}>
                            {isCredit ? "Credit" : "Debit"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: isCredit ? "#22c55e" : "#ef4444" }}>
                          {isCredit ? "+" : "-"}₹{(tx.amount || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tx.description || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Chip
                          label={tx.status || "—"}
                          size="small"
                          color={statusColor[tx.status] || "default"}
                          sx={{ fontWeight: 600, fontSize: 10 }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalTx}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={limit}
          rowsPerPageOptions={[10]}
        />
      </Card>

      {/* Add Funds Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#442141" }}>Add Funds to Wallet</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Amount (₹)"
            value={amount}
            onChange={handleAmountChange}
            fullWidth
            inputProps={{ inputMode: "decimal" }}
            error={!!amountError}
            helperText={amountError || "Enter amount to credit"}
            autoFocus
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            placeholder="e.g. Admin credit"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: "#442141" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddFunds}
            disabled={!!amountError || !amount || adding}
            sx={{ background: "#442141", "&:hover": { background: "#5a2d57" } }}
          >
            {adding ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Add Funds"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
